import React from 'react';
import { styled, css } from '@compiled/react';
import QuestionCircleIcon from '@atlaskit/icon/core/migration/question-circle';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import commonMessages from '../../../common/messages.tsx';
import type { Count, DeploymentFrequencyReportWithProviderInfo } from '../../../common/types.tsx';
import { isDataUnmappedFoundWithMissingPreProdButWithProd } from '../../../services/deployment-frequency-core/utils.tsx';
import developmentIcon from './assets/development.svg';
import productionIcon from './assets/production.svg';
import stagingIcon from './assets/staging.svg';
import testingIcon from './assets/testing.svg';
import unmappedIcon from './assets/unmapped.png';
import messages from './messages.tsx';
import { getPotentialCounts } from './utils.tsx';

type Props = {
	deploymentReportData: DeploymentFrequencyReportWithProviderInfo;
	selectedWeekStart: Date;
};

type EnvironmentLegendProps = {
	shouldDisplayTooltipForUnmappedLegend: boolean;
	iconSrc: string;
	environment: 'development' | 'testing' | 'staging' | 'production' | 'unmapped';
};

const EnvironmentLegend = ({
	iconSrc,
	environment,
	shouldDisplayTooltipForUnmappedLegend,
}: EnvironmentLegendProps) => {
	const { formatMessage } = useIntl();

	return (
		<LegendRow>
			<LegendIcon src={iconSrc} alt="" />
			<LegendLabel>{formatMessage(commonMessages[environment])}</LegendLabel>
			{shouldDisplayTooltipForUnmappedLegend && (
				<Box xcss={tooltipContainerStyles}>
					<Tooltip content={formatMessage(messages.unmappedTooltip)} position="right">
						<QuestionIcon>
							<QuestionCircleIcon LEGACY_size="small" label="" />
						</QuestionIcon>
					</Tooltip>
				</Box>
			)}
		</LegendRow>
	);
};

// do not provide anything to not render.
type ContentWithCountProps = {
	developmentCount?: Count | '-';
	testingCount?: Count | '-';
	stagingCount?: Count | '-';
	productionCount?: Count | '-';
	unmappedCount?: Count | '-';
};

type LeftContentProps = {
	shouldDisplayTooltipForUnmappedLegend: boolean;
} & ContentWithCountProps;

const LeftContent = ({
	developmentCount,
	testingCount,
	stagingCount,
	productionCount,
	unmappedCount,
	shouldDisplayTooltipForUnmappedLegend,
}: LeftContentProps) => (
	<>
		{developmentCount !== undefined && (
			<EnvironmentLegend
				iconSrc={developmentIcon}
				environment="development"
				shouldDisplayTooltipForUnmappedLegend={false}
			/>
		)}
		{testingCount !== undefined && (
			<EnvironmentLegend
				iconSrc={testingIcon}
				environment="testing"
				shouldDisplayTooltipForUnmappedLegend={false}
			/>
		)}
		{stagingCount !== undefined && (
			<EnvironmentLegend
				iconSrc={stagingIcon}
				environment="staging"
				shouldDisplayTooltipForUnmappedLegend={false}
			/>
		)}
		{productionCount !== undefined && (
			<EnvironmentLegend
				iconSrc={productionIcon}
				environment="production"
				shouldDisplayTooltipForUnmappedLegend={false}
			/>
		)}
		{unmappedCount !== undefined && (
			<>
				<EnvironmentLegend
					iconSrc={unmappedIcon}
					environment="unmapped"
					shouldDisplayTooltipForUnmappedLegend={shouldDisplayTooltipForUnmappedLegend}
				/>
			</>
		)}
	</>
);

const RightContent = ({
	developmentCount,
	testingCount,
	stagingCount,
	productionCount,
	unmappedCount,
}: ContentWithCountProps) => (
	<>
		{developmentCount !== undefined && (
			<WeekDeploymentCountRow>{developmentCount}</WeekDeploymentCountRow>
		)}
		{testingCount !== undefined && <WeekDeploymentCountRow>{testingCount}</WeekDeploymentCountRow>}
		{stagingCount !== undefined && <WeekDeploymentCountRow>{stagingCount}</WeekDeploymentCountRow>}
		{productionCount !== undefined && (
			<WeekDeploymentCountRow>{productionCount}</WeekDeploymentCountRow>
		)}
		{unmappedCount !== undefined && (
			<WeekDeploymentCountRow>{unmappedCount}</WeekDeploymentCountRow>
		)}
	</>
);

export const Legend = ({ deploymentReportData, selectedWeekStart }: Props) => {
	const { formatMessage } = useIntl();
	const { developmentCount, testingCount, stagingCount, productionCount, unmappedCount } =
		getPotentialCounts(deploymentReportData, selectedWeekStart);
	const shouldDisplayTooltipForUnmappedLegend =
		isDataUnmappedFoundWithMissingPreProdButWithProd(deploymentReportData);

	return (
		<Wrapper>
			<LeftContainer>
				<TitleLeftContainer>{formatMessage(messages.environmentTypeTitle)}</TitleLeftContainer>
				<LeftContent
					developmentCount={developmentCount}
					testingCount={testingCount}
					stagingCount={stagingCount}
					productionCount={productionCount}
					unmappedCount={unmappedCount}
					shouldDisplayTooltipForUnmappedLegend={shouldDisplayTooltipForUnmappedLegend}
				/>
			</LeftContainer>
			<RightContainer>
				<TitleRightContainer>{formatMessage(messages.weeklyDeploymentTitle)}</TitleRightContainer>
				<RightContent
					developmentCount={developmentCount}
					testingCount={testingCount}
					stagingCount={stagingCount}
					productionCount={productionCount}
					unmappedCount={unmappedCount}
				/>
			</RightContainer>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftContainer = styled.div({
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	flex: 1,
});

const rowCommonStyles = css({
	marginBottom: token('space.100'),
	height: token('space.200'),
});

const titleCommonStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
	font: token('font.body.small'),
	fontWeight: token('font.weight.bold'),
	height: token('space.200'),
	marginBottom: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const TitleLeftContainer = styled.div(titleCommonStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const TitleRightContainer = styled.div(titleCommonStyles, {
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegendRow = styled.div(
	{
		display: 'flex',
		alignItems: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	rowCommonStyles,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegendIcon = styled.img({
	width: token('space.200'),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegendLabel = styled.span<any>({
	marginLeft: token('space.050'),
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const WeekDeploymentCountRow = styled.div(rowCommonStyles, {
	textAlign: 'center',
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const QuestionIcon = styled.span({
	marginLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fill: token('color.icon.inverse', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N80),
	cursor: 'pointer',
});

const tooltipContainerStyles = xcss({
	marginBottom: 'space.negative.050',
});
